import axios, {AxiosHeaders, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig} from 'axios';
import Config from './Config';
import cookie from './cookie';
import {Constants} from './constants';
import {Utility} from "./utility";
import { isArray, get } from 'lodash';
import {Endpoints} from "./endpoints";
import AuthService from "../services/AuthService";

const createHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const session = cookie.get(Constants.ACCESS_KEY);
    const csrftoken = cookie.get(Constants.CSRF_KEY);

    config.headers.set('Accept', 'application/json');

    if(!config.headers.get('Content-Type'))
        config.headers.set('Content-Type', 'application/json;charset=UTF-8');

    if (session) config.headers.set('Authorization', `Bearer ${session}`);
    if (csrftoken) config.headers.set('requesttoken', csrftoken);
    return config;
};

const http: AxiosInstance = axios.create({
    baseURL: Config.api,
    headers: createHeaders({headers: new AxiosHeaders()}).headers,
    transformResponse: [
        (data) => {
            const isoDateRegex = /^\d{4}-\d{2}-\d{2}([T\s]\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
            const jsonData = JSON.parse(data);
            const convertDates = (obj: any) => {
                if (obj && typeof obj === 'object') {
                    for (const key in obj) {
                        if (Object.prototype.hasOwnProperty.call(obj, key)) {
                            const value = obj[key];
                            if (typeof value === 'string' && isoDateRegex.test(value)) {
                                obj[key] = new Date(value);
                            } else if (typeof value === 'object') {
                                convertDates(value);
                            }
                        }
                    }
                }
                return obj;
            };
            return convertDates(jsonData);
        }
    ]
});

const isUrlEncoded = (url?: string): boolean => {
    return [
        Endpoints.LOG_IN,
    ].includes(url as Endpoints);
};

const applyTenantBaseURL = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const requestTenant = (config.data as any)?.tenant;
    const tenant = requestTenant || cookie.get('tenant') || cookie.get('last_platform');

    if (!tenant || !config.baseURL) {
        return config;
    }

    if (config.baseURL.includes('{tenant}')) {
        config.baseURL = config.baseURL.replace('{tenant}', tenant);
        return config;
    }

    try {
        const url = new URL(config.baseURL);

        if (!url.hostname.startsWith(`${tenant}.`)) {
            config.baseURL = Utility.replaceSubdomain(config.baseURL, tenant);
        }
    } catch {
        return config;
    }

    return config;
};

// Request interceptor for API calls
http.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    config = createHeaders(config);
    config = applyTenantBaseURL(config);

    const { tenant } = config.data || {};
    if (tenant) {
        cookie.remove('tenant');
        cookie.put('tenant', tenant);
    }

    config.headers.set(
        'Content-Type',
        isUrlEncoded(config.url)
            ? 'application/x-www-form-urlencoded'
            : config.headers.get('Content-Type')
    );

    return config;
});

// Response interceptor for API calls
http.interceptors.response.use((response: AxiosResponse) => {
    return response
}, async function (error) {
    const originalRequest = error.config;
    const apiCode = get(error.response, 'data.code');

    // Dedicated handling for forced password updates before generic 403 refresh flow.
    if (get(error.response, 'status') === 403 && apiCode === Constants.PASSWORD_CHANGE_REQUIRED) {
        let requestData: any = {};
        const rawData = originalRequest?.data;

        try {
            requestData = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || {});
        } catch (e) {
            requestData = Object.fromEntries(new URLSearchParams(rawData));
        }

        const tenant = requestData?.tenant || cookie.get('tenant') || cookie.get('last_platform');
        const login = requestData?.login || AuthService.getUser().login;

        cookie.put(Constants.AUTH_ERROR_CODE_COOKIE, Constants.PASSWORD_CHANGE_REQUIRED, "Lax", true);

        if (tenant) {
            cookie.put(Constants.PENDING_PLATFORM_COOKIE, tenant, "Lax", true);
        } else {
            cookie.remove(Constants.PENDING_PLATFORM_COOKIE);
        }

        if (login) {
            cookie.put(Constants.PENDING_LOGIN_COOKIE, login, "Lax", true);
        } else {
            cookie.remove(Constants.PENDING_LOGIN_COOKIE);
        }

        if (tenant && login) {
            window.location.replace(`${Config.url}password-change-required`);
        } else {
            window.location.replace(`${Config.url}login`);
        }

        return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
        Utility.clearLoginCookies()
        Utility.redirectToLogin();
    }

    if (error.response && error.response.status === 500) {
        const errMsg: string = get(error.response, 'data.message');
        if(errMsg.includes('Tenant could not be identified on domain')){
            cookie.remove('tenant')
            window.location.replace('/tenant-not-found')
        }
    }

    if (get(error.response, 'status') === 403 && get(error.response, 'config.url') !== "login/confirm" && !originalRequest._retry) {
        const access_token = await Utility.refreshToken();
        if(!access_token){
            cookie.remove(Constants.ACCESS_KEY)
            cookie.remove(Constants.REFRESH_KEY)
        }
        originalRequest._retry = true;
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + access_token;
        return http(originalRequest);
    }

    return Promise.reject(error);
});

// Create a new Axios instance for multipart form data requests
const multipartHttp: AxiosInstance = axios.create({
    baseURL: Config.api,
});

// Request interceptor for multipart form data requests
multipartHttp.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    config = createHeaders(config);
    config = applyTenantBaseURL(config);
    config.headers['Content-Type'] = 'multipart/form-data';
    return config;
});

export {multipartHttp};

export const parseRequestData = (data: any, method: string = ''): any => {
    if (isArray(data)) {
        return data.join(',');
    }

    if (method === 'multipart') {//NOTE case?
        const fd = new FormData();
        for (const key in data) {
            if(isArray(data[key])){
                data[key].forEach((file: File, index: number) => {
                    fd.append(`files[${index}]`, file);
                });
            } else {
                fd.append(key, data[key])
            }
        }

        return fd;
    }

    if (method === 'get') {//NOTE case?
        const params = new URLSearchParams();
        for (const key in data) {
            params.append(key, data[key])
        }
        return params.toString();
    }
    return data;
};

export default http;
